import React, { useState, useEffect, useRef } from "react";
import { api } from '../../utils/api';
import { COLORS } from "../../constants/colors";
import Chart from "chart.js/auto";
import CircularProgress from "@mui/material/CircularProgress";

const roundToNiceNumber = (num) => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(num)));
    const normalized = num / magnitude;
    let niceNumber;
    
    if (normalized <= 2) niceNumber = 2;
    else if (normalized <= 5) niceNumber = 5;
    else niceNumber = 10;
    
    return Math.ceil(niceNumber * magnitude);
};

export default function UserAgeDistributionChart({ userData }) {
    const chartRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ageDistribution, setAgeDistribution] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Add resize listener to update mobile state
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchAgeDistribution = async () => {
            if (!userData?.accountId) {
                setError("No account ID available");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await api.get(`/api/metrics/users/${userData.accountId}/age`);
                
                if (response && typeof response === 'object') {
                    let distribution = response.age_distribution || response;
                    setAgeDistribution(distribution);
                    setError(null);
                } else {
                    throw new Error("Invalid response format");
                }
            } catch (err) {
                console.error('Error fetching age distribution:', err);
                setError(err.message || "Failed to fetch age distribution");
            } finally {
                setLoading(false);
            }
        };

        fetchAgeDistribution();
    }, [userData?.accountId]);

    useEffect(() => {
        let chartInstance = null;
        
        if (!loading && ageDistribution && Object.keys(ageDistribution).length > 0 && chartRef.current) {
            // Process the age distribution data
            const processedDistribution = {};
            let sixtyPlusCount = 0;
            
            // Group all ages 60+ together and keep others as is
            Object.entries(ageDistribution).forEach(([age, count]) => {
                // Skip 'Unknown' values entirely
                if (age !== 'Unknown' && age !== null) {
                    const ageNum = parseInt(age);
                    if (!isNaN(ageNum)) {
                        if (ageNum >= 60) {
                            sixtyPlusCount += count;
                        } else {
                            processedDistribution[age] = count;
                        }
                    }
                }
            });
            
            // Add the 60+ group if there are any users
            if (sixtyPlusCount > 0) {
                processedDistribution['60+'] = sixtyPlusCount;
            } else {
                // Add 60+ with zero count to ensure it appears
                processedDistribution['60+'] = 0;
            }
            
            // Create array with ALL ages from 18 to 59, regardless of data
            const allAges = [];
            for (let age = 21; age <= 59; age++) {
                allAges.push(age.toString());
            }
            
            // Add '60+' only
            allAges.push('60+');
            
            const labels = allAges;
            const values = allAges.map(age => processedDistribution[age] || 0);
            const maxValue = Math.max(...values.filter(v => v > 0)) || 1; // Fallback to 1 if all values are 0
            
            // Force destroy any existing chart
            if (Chart.getChart(chartRef.current)) {
                Chart.getChart(chartRef.current).destroy();
            }
            
            chartInstance = new Chart(chartRef.current, {
                type: "bar",
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: "Patrons",
                            data: values,
                            backgroundColor: COLORS.PURPLE.hex,
                            borderColor: COLORS.PURPLE.hex,
                            borderWidth: 1,
                            borderRadius: 6,
                            // Add hover effects
                            hoverBackgroundColor: COLORS.PURPLE.hex + 'DD',
                            hoverBorderColor: COLORS.PURPLE.hex,
                            hoverBorderWidth: 2,
                            // Add number counter animation
                            onProgress: function(animation) {
                                const chart = animation.chart;
                                chart.data.datasets.forEach((dataset, i) => {
                                    const meta = chart.getDatasetMeta(i);
                                    meta.data.forEach((bar, index) => {
                                        const value = dataset.data[index];
                                        if (value > 0) {
                                            const progress = animation.currentStep / animation.numSteps;
                                            const displayValue = Math.round(value * progress);
                                            if (displayValue > 0) {
                                                chart.ctx.fillStyle = '#1e293b';
                                                chart.ctx.textAlign = 'center';
                                                chart.ctx.textBaseline = 'bottom';
                                                chart.ctx.font = (isMobile ? '10px' : '12px') + " 'Inter', sans-serif";
                                                chart.ctx.fillText(displayValue, bar.x, bar.y - 5);
                                            }
                                        }
                                    });
                                });
                            }
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            left: isMobile ? 5 : 20,
                            right: isMobile ? 5 : 20,
                            top: 0,
                            bottom: isMobile ? 10 : 20
                        }
                    },
                    // Enhanced animations
                    animation: {
                        duration: 2000,
                        easing: 'easeOutElastic',
                        delay: (context) => context.dataIndex * 100,
                    },
                    // Add transitions for smoother updates
                    transitions: {
                        active: {
                            animation: {
                                duration: 400,
                                easing: 'easeOutQuart'
                            }
                        },
                        resize: {
                            animation: {
                                duration: 500,
                                easing: 'easeOutBounce'
                            }
                        },
                        show: {
                            animations: {
                                y: {
                                    from: 500,
                                    delay: (ctx) => ctx.dataIndex * 100
                                },
                                opacity: {
                                    from: 0
                                }
                            }
                        },
                        hide: {
                            animations: {
                                y: {
                                    to: 500
                                },
                                opacity: {
                                    to: 0
                                }
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: false
                        },
                        title: {
                            display: true,
                            text: "Age Distribution",
                            font: {
                                size: isMobile ? 18 : 30,
                                family: "'Inter', sans-serif",
                                weight: 'bold'
                            },
                            padding: { 
                                top: 0,
                                bottom: isMobile ? 10 : 20
                            },
                            color: '#1e293b'
                        },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#1e293b',
                            bodyColor: '#1e293b',
                            borderColor: '#e2e8f0',
                            borderWidth: 1,
                            padding: isMobile ? 8 : 12,
                            titleFont: {
                                size: isMobile ? 12 : 14,
                                weight: 'bold',
                                family: "'Inter', sans-serif"
                            },
                            bodyFont: {
                                size: isMobile ? 11 : 13,
                                family: "'Inter', sans-serif"
                            },
                            callbacks: {
                                label: (context) => {
                                    const value = context.raw;
                                    const total = values.reduce((sum, val) => sum + val, 0);
                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                                    const patronText = value === 1 ? "patron" : "patrons";
                                    return `${value} ${patronText} (${percentage}%)`;
                                },
                            },
                            // Add animation to tooltips
                            animation: {
                                duration: 200
                            }
                        },
                        datalabels: {
                            display: false
                        },
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                autoSkip: false,
                                font: {
                                    size: isMobile ? 10 : 14,
                                    family: "'Inter', sans-serif",
                                    weight: '600'
                                },
                                color: '#64748b',
                                maxRotation: isMobile ? 45 : 0,
                                minRotation: isMobile ? 45 : 0,
                                padding: isMobile ? 2 : 8,
                                // Only show labels every 3 ages
                                callback: function(value, index, values) {
                                    const label = this.getLabelForValue(value);
                                    // Always show 'Unknown' and '60+' labels
                                    if (label === 'Unknown' || label === '60+') return label;
                                    
                                    // For numeric ages, show every 3rd label
                                    return index % 3 === 0 ? label : '';
                                }
                            }
                        },
                        y: {
                            beginAtZero: true,
                            grid: {
                                display: false,
                                color: '#f1f5f9',
                                lineWidth: 1
                            },
                            border: {
                                display: false
                            },
                            ticks: {
                                font: {
                                    size: isMobile ? 10 : 14,
                                    family: "'Inter', sans-serif",
                                    weight: '600'
                                },
                                color: '#64748b',
                                padding: isMobile ? 5 : 10,
                                precision: 0,
                                // Use fewer ticks on mobile
                                maxTicksLimit: isMobile ? 5 : 10,
                                stepSize: isMobile ? Math.ceil(maxValue / 4) : 1,
                                // Ensure whole numbers only
                                callback: function(value) {
                                    if (Math.floor(value) === value) {
                                        return value;
                                    }
                                }
                            },
                            min: 0,
                            suggestedMax: roundToNiceNumber(maxValue),
                            // Add animation to y-axis
                            animate: true
                        }
                    },
                    // Add hover animation
                    hover: {
                        mode: 'index',
                        intersect: false,
                        animationDuration: 300
                    },
                    // Add interaction settings
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    // Add element-specific animations
                    elements: {
                        bar: {
                            // Add custom animation for bars
                            animate: true,
                            // Add shadow on hover
                            hoverShadowBlur: 10,
                            hoverShadowColor: 'rgba(0,0,0,0.3)'
                        }
                    }
                }
            });
        }

        return () => {
            if (chartInstance) {
                chartInstance.destroy();
            }
        };
    }, [ageDistribution, loading, isMobile]);

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <CircularProgress size={isMobile ? 30 : 40} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorMessage(isMobile)}>Error loading data: {error}</div>
            </div>
        );
    }

    return (
        <div style={styles.chartContainer(isMobile)}>
            <canvas ref={chartRef}></canvas>
        </div>
    );
}

const styles = {
    container: {
        padding: window.innerWidth < 768 ? "8px" : "24px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        margin: window.innerWidth < 768 ? "4px" : "20px",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: window.innerWidth < 768 ? "300px" : "500px",
    },
    errorMessage: (isMobile) => ({
        color: "#d32f2f",
        padding: isMobile ? "8px" : "20px",
        textAlign: "center",
        fontWeight: "bold",
        fontSize: isMobile ? "12px" : "16px",
    }),
    chartContainer: (isMobile) => ({
        width: "100%",
        height: isMobile ? "300px" : "400px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        paddingTop: 0,
    }),
};

